import React from 'react';

import { useSelector } from 'react-redux';

import InfoIcon from 'cccisd-icons/info2';
import { ResponsiveBar } from 'cccisd-nivo/bar';
import Tooltip from 'cccisd-tooltip';

import style from './style.css';

const YOUTH_COLOR = '#806fa6';
const CAREGIVER_COLOR = '#e7a32a';
const isSupervisor = window.cccisd?.fortress?.user?.acting?.role?.handle === 'supervisor';

// Flattens scoreAverages (planName > timepoint > domain > pawnId) into per-domain
// averages for youth and caregiver, weighted by care coordinator.
// Each Care Coordinator's respondents are averaged first, then those Care Coordinator-level averages are averaged,
// so every Care Coordinator contributes equally regardless of caseload size.
const getChartData = scoreAverages => {
    // Build youthPawnId -> Care Coordinator pawnIds map from youth entries
    const youthCareCoordinatorMap = {};
    Object.values(scoreAverages).forEach(timepointData =>
        Object.values(timepointData).forEach(domainData =>
            Object.values(domainData).forEach(pawnAverageData =>
                Object.entries(pawnAverageData).forEach(([pawnId, entry]) => {
                    if (entry.role === 'youth' && !youthCareCoordinatorMap[pawnId]) {
                        youthCareCoordinatorMap[pawnId] = (entry.pawn?.parentRoles?.careCoordinatorList || []).map(
                            cc => cc.pawn.pawnId
                        );
                    }
                })
            )
        )
    );

    // Accumulate per-pawn averages into per-CC buckets per domain/role
    // { [domain]: { youth: { [careCoordinatorKey]: { sum, count } }, caregiver: { [careCoordinatorKey]: ... } } }
    const domainCareCoordinatorTotals = {};

    Object.values(scoreAverages).forEach(timepointData => {
        Object.values(timepointData).forEach(domainData => {
            Object.entries(domainData).forEach(([domain, pawnAverageData]) => {
                if (!domainCareCoordinatorTotals[domain]) {
                    domainCareCoordinatorTotals[domain] = { youth: {}, caregiver: {} };
                }
                Object.entries(pawnAverageData).forEach(([pawnId, entry]) => {
                    const { role } = entry;
                    if (role === 'youth' || role === 'caregiver') {
                        const count = entry.numberOfQuestionsAnswered || 0;
                        if (count > 0) {
                            const pawnAvg = entry.sum / count;
                            // For caregivers the key is caregiverPawnId_youthPawnId; look up CC via the youth
                            const youthPawnId = role === 'youth' ? pawnId : pawnId.split('_')[1];
                            const careCoordinatorPawnIds = youthCareCoordinatorMap[youthPawnId] || [];
                            const careCoordinatorKeys =
                                careCoordinatorPawnIds.length > 0 ? careCoordinatorPawnIds : ['__none__'];
                            careCoordinatorKeys.forEach(careCoordinatorKey => {
                                if (!domainCareCoordinatorTotals[domain][role][careCoordinatorKey]) {
                                    domainCareCoordinatorTotals[domain][role][careCoordinatorKey] = {
                                        sum: 0,
                                        count: 0,
                                    };
                                }
                                domainCareCoordinatorTotals[domain][role][careCoordinatorKey].sum += pawnAvg;
                                domainCareCoordinatorTotals[domain][role][careCoordinatorKey].count += 1;
                            });
                        }
                    }
                });
            });
        });
    });

    // Average the CC-level averages to get each domain's bar height.
    // -1 means no data for that role in this domain (filtered out in the chart render).
    const computeFromCareCoordinatorGroups = ccData => {
        const careCoordinatorAverages = Object.values(ccData)
            .filter(cc => cc.count > 0)
            .map(cc => cc.sum / cc.count);

        return careCoordinatorAverages.length > 0
            ? +(careCoordinatorAverages.reduce((a, b) => a + b, 0) / careCoordinatorAverages.length).toFixed(2)
            : -1;
    };

    return Object.entries(domainCareCoordinatorTotals).map(([domain, roles]) => ({
        domain,
        youth: computeFromCareCoordinatorGroups(roles.youth),
        caregiver: computeFromCareCoordinatorGroups(roles.caregiver),
    }));
};

const average = scores => {
    const res = scores.length > 0 ? +(scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(2) : 0;

    return res;
};

// match domain order in individual tab
const DOMAIN_ORDER = ['alliance', 'fidelity', 'satisfaction', 'progress', 'outcomesCaregiver', 'outcomes'];

const formatDomainLabel = domain => {
    if (domain === 'outcomesCaregiver') return 'Family Outcomes';
    if (domain === 'outcomes') return 'Youth Outcomes';
    return domain.charAt(0).toUpperCase() + domain.slice(1);
};

const AggregateAverages = () => {
    const aggregateScoreAverages = useSelector(state => state.app.smartwrap.dashboard.aggregateScoreAverages);

    if (!aggregateScoreAverages) return <></>;

    const chartData = getChartData(aggregateScoreAverages).sort(
        (a, b) => DOMAIN_ORDER.indexOf(a.domain) - DOMAIN_ORDER.indexOf(b.domain)
    );

    // y-axis always reaches at least 3.0; extends further if any bar exceeds it
    // ignore values of -1
    const chartMax = Math.max(3, ...chartData.flatMap(d => [d.youth, d.caregiver]).filter(v => v >= 0));

    // Overall averages used as dashed reference lines across all domains
    // ignore values of -1
    const youthAverage = average(
        chartData.map(domainAverageData => domainAverageData.youth).filter(averageValue => averageValue >= 0)
    );
    const caregiverAverage = average(
        chartData.map(domainAverageData => domainAverageData.caregiver).filter(averageValue => averageValue >= 0)
    );

    return (
        <div className={style.aggregate_graph_wrapper}>
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <h5 style={{ marginRight: '10px', marginLeft: '10px' }}>Survey Ratings Across Domains</h5>
                <Tooltip
                    title={
                        isSupervisor
                            ? "Each bar shows the average score for that domain, weighted by care coordinator. Each respondent's score is averaged first, then those are averaged per care coordinator, and finally the care coordinator averages are averaged together. Example: CC1 has two youths scoring 1.25 each (CC avg = 1.25), CC2 has one youth scoring 2.5 (CC avg = 2.5) — domain average = (1.25 + 2.5) / 2 = 1.88"
                            : "Each bar shows the average score for that domain across all respondents and timepoints. Each respondent's score is averaged first (sum / questions answered), then those averages are averaged together. Example: user A scored 2.5, user B scored 1.0 — domain average = (2.5 + 1.0) / 2 = 1.75"
                    }
                    placement="top"
                >
                    <InfoIcon />
                </Tooltip>
            </div>
            <div className={style.aggregate_graph}>
                <ResponsiveBar
                    // filter out values of -1, which represent youths or carevivers not having any responses in a domain at all
                    // also replace values of 0 with .05 so that the bar graph visually shows a tiny bar for a domain with 0 score
                    data={chartData.map(domainData =>
                        Object.fromEntries(
                            Object.entries(domainData)
                                .filter(([, value]) => value !== -1)
                                .map(([domain, value]) => [domain, value === 0 ? 0.05 : value])
                        )
                    )}
                    keys={['youth', 'caregiver']}
                    indexBy="domain"
                    groupMode="grouped"
                    colors={({ id }) => (id === 'youth' ? YOUTH_COLOR : CAREGIVER_COLOR)}
                    labelTextColor="#17191c"
                    theme={{ labels: { text: { fontSize: 16 } } }}
                    maxValue={chartMax}
                    layers={[
                        'grid',
                        'axes',
                        'bars',
                        'markers',
                        'legends',
                        ({ bars }) => (
                            // show values of 0 when the data average is actually 0 from real responses
                            <>
                                {bars
                                    .filter(bar => bar.data.value === 0.05)
                                    .map(bar => {
                                        return (
                                            <text
                                                key={bar.key}
                                                x={bar.x + bar.width / 2}
                                                y={bar.y - 6}
                                                textAnchor="middle"
                                                style={{
                                                    fontSize: 14,
                                                    fontWeight: 'bold',
                                                    fontStyle: 'italic',
                                                    fill:
                                                        (bar.id ?? bar.data?.id) === 'youth'
                                                            ? YOUTH_COLOR
                                                            : CAREGIVER_COLOR,
                                                }}
                                            >
                                                0
                                            </text>
                                        );
                                    })}
                            </>
                        ),
                    ]}
                    markers={[
                        {
                            axis: 'y',
                            value: youthAverage,
                            lineStyle: {
                                stroke: YOUTH_COLOR,
                                strokeWidth: 2,
                                strokeDasharray: '4 4',
                            },
                        },
                        {
                            axis: 'y',
                            value: caregiverAverage,
                            lineStyle: {
                                stroke: CAREGIVER_COLOR,
                                strokeWidth: 2,
                                strokeDasharray: '4 4',
                            },
                        },
                    ]}
                    margin={{ top: 10, right: 20, bottom: 80, left: 50 }}
                    axisBottom={{
                        legend: 'Domain',
                        legendPosition: 'middle',
                        legendOffset: 40,
                        format: formatDomainLabel,
                    }}
                    axisLeft={{
                        legend: 'Average Score',
                        legendPosition: 'middle',
                        legendOffset: -40,
                    }}
                    legends={[
                        {
                            dataFrom: 'keys',
                            anchor: 'bottom',
                            direction: 'row',
                            justify: false,
                            translateX: 25,
                            translateY: 70,
                            itemsSpacing: 20,
                            itemWidth: 100,
                            itemHeight: 20,
                            itemDirection: 'left-to-right',
                            itemOpacity: 0.85,
                            symbolSize: 20,
                        },
                    ]}
                />
            </div>
        </div>
    );
};

export default AggregateAverages;
